// CourseGrowthTile — Progress Dash tile that answers the operator
// question "how fast are we actually adding new degrees per country?"
//
// Fed by `/api/admin/progress/course-growth`. Each row shows:
//   • country name
//   • current total degrees in the catalogue
//   • 24h and 7d additions (NEW rows via wave or seed since Aug 2026)
//   • when the last course was added
//   • whether a wave exists AND when it last ran
//
// Rows are returned thin-first by the backend so the eye lands on the
// countries that most need attention. A tiny KPI strip at the top
// shows catalogue totals + total adds in 24h / 7d.
//
// The tile polls every 60s so the operator can watch the number climb
// during a themed night. No websocket needed — 60s is slow enough that
// a nightly addition rate of ~1 row every few seconds reads smoothly.
import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Loader2, TrendingUp, AlertCircle, CheckCircle2, Zap } from "lucide-react";

function _fmt(n) {
  return n == null ? "—" : Number(n).toLocaleString();
}

function _timeAgo(iso) {
  if (!iso) return null;
  const then = new Date(iso).getTime();
  if (!then) return null;
  const diffS = Math.max(0, Math.floor((Date.now() - then) / 1000));
  if (diffS < 60) return `${diffS}s ago`;
  if (diffS < 3600) return `${Math.floor(diffS / 60)}m ago`;
  if (diffS < 86400) return `${Math.floor(diffS / 3600)}h ago`;
  return `${Math.floor(diffS / 86400)}d ago`;
}

export default function CourseGrowthTile() {
  const [data, setData] = useState(null);
  const [err, setErr]   = useState(null);
  const [loading, setLoading] = useState(true);
  const mounted = useRef(true);

  useEffect(() => {
    let timer;
    async function load() {
      try {
        const { data } = await api.get("/admin/progress/course-growth");
        if (!mounted.current) return;
        setData(data);
        setErr(null);
      } catch (e) {
        if (!mounted.current) return;
        setErr(e?.response?.data?.detail || e?.message || "Could not load course-growth");
      } finally {
        if (mounted.current) setLoading(false);
      }
    }
    load();
    timer = setInterval(load, 60_000);
    return () => {
      mounted.current = false;
      clearInterval(timer);
    };
  }, []);

  if (loading && !data) {
    return (
      <div className="bg-white border border-stone-200 rounded-lg p-6 flex items-center gap-3 text-sm text-stone-500"
           data-testid="course-growth-loading">
        <Loader2 className="h-4 w-4 animate-spin" />
        Measuring course-growth…
      </div>
    );
  }

  if (err && !data) {
    return (
      <div className="bg-rose-50 border border-rose-200 rounded-lg p-6 text-sm text-rose-800"
           data-testid="course-growth-error">
        <AlertCircle className="h-4 w-4 inline-block mr-2" />
        {err}
      </div>
    );
  }

  const cat = data?.catalogue || {};
  const rows = data?.rows || [];

  return (
    <div className="bg-white border border-stone-200 rounded-lg overflow-hidden"
         data-testid="course-growth-tile">
      <div className="px-6 py-4 border-b border-stone-200 flex items-center justify-between">
        <div>
          <div className="text-[10px] font-underground-sc tracking-widest text-stone-500">
            Course growth · live
          </div>
          <div className="font-editorial text-xl text-[#002147] mt-1">
            How fast are we adding degrees?
          </div>
        </div>
        <div className="hidden sm:flex gap-6 text-right">
          <div>
            <div className="text-[10px] font-underground-sc tracking-widest text-stone-500">Total catalogue</div>
            <div className="font-mono text-xl text-[#002147]" data-testid="course-growth-total">
              {_fmt(cat.total)}
            </div>
          </div>
          <div>
            <div className="text-[10px] font-underground-sc tracking-widest text-stone-500">Added 24h</div>
            <div className={`font-mono text-xl ${cat.added_24h ? "text-emerald-700" : "text-stone-400"}`}
                 data-testid="course-growth-added-24h">
              {cat.added_24h ? `+${_fmt(cat.added_24h)}` : "0"}
            </div>
          </div>
          <div>
            <div className="text-[10px] font-underground-sc tracking-widest text-stone-500">Added 7d</div>
            <div className={`font-mono text-xl ${cat.added_7d ? "text-emerald-700" : "text-stone-400"}`}
                 data-testid="course-growth-added-7d">
              {cat.added_7d ? `+${_fmt(cat.added_7d)}` : "0"}
            </div>
          </div>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-stone-50 text-[10px] font-underground-sc tracking-widest text-stone-500 uppercase">
            <tr>
              <th className="px-6 py-2 text-left">Country</th>
              <th className="px-4 py-2 text-right">Total</th>
              <th className="px-4 py-2 text-right">24h</th>
              <th className="px-4 py-2 text-right">7d</th>
              <th className="px-4 py-2 text-left">Last added</th>
              <th className="px-4 py-2 text-left">Wave</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-stone-100">
            {rows.map((r) => (
              <tr key={r.country} className="hover:bg-stone-50" data-testid={`course-growth-row-${r.country}`}>
                <td className="px-6 py-2 font-sans text-[#002147]">{r.country}</td>
                <td className="px-4 py-2 text-right font-mono tabular-nums">{_fmt(r.total)}</td>
                <td className={`px-4 py-2 text-right font-mono tabular-nums ${r.added_24h ? "text-emerald-700 font-semibold" : "text-stone-400"}`}>
                  {r.added_24h ? `+${_fmt(r.added_24h)}` : "·"}
                </td>
                <td className={`px-4 py-2 text-right font-mono tabular-nums ${r.added_7d ? "text-emerald-700" : "text-stone-400"}`}>
                  {r.added_7d ? `+${_fmt(r.added_7d)}` : "·"}
                </td>
                <td className="px-4 py-2 text-stone-500 text-xs">
                  {r.last_added ? _timeAgo(r.last_added) : "—"}
                </td>
                <td className="px-4 py-2 text-xs">
                  {r.has_wave ? (
                    <span className="inline-flex items-center gap-1 text-stone-700">
                      <Zap className="h-3 w-3 text-amber-600" />
                      {r.wave_name}
                      {r.last_wave_run?.status === "ok" && <CheckCircle2 className="h-3 w-3 text-emerald-600" />}
                    </span>
                  ) : (
                    <span className="text-rose-700 text-[11px]">no wave wired</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="px-6 py-3 bg-stone-50 border-t border-stone-200 text-[11px] text-stone-500 flex items-center gap-2">
        <TrendingUp className="h-3 w-3" />
        Refreshes every 60 seconds. 24h/7d counts measure NEW adds (rows with a
        `created_at` timestamp). Historical seed rows contribute to the total only.
      </div>
    </div>
  );
}
